import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { apiClient } from '../lib/api';
import { Sparkles, Eye, EyeOff, Loader2, CheckCircle, XCircle, ArrowLeft, Lock } from 'lucide-react';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check for token on mount
  useEffect(() => {
    if (!token) {
      setError('Lien de réinitialisation invalide. Veuillez demander un nouveau lien.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError('Lien de réinitialisation invalide');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/auth/reset-password', {
        token: token,
        new_password: newPassword
      });

      if (response.data.success) {
        setSuccess(true);
        toast.success('Mot de passe réinitialisé avec succès !');
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/auth');
        }, 3000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Échec de la réinitialisation. Veuillez réessayer.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1762433813475-e6b761cc23d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMHNvZnQlMjBwYXN0ZWwlMjBjb2xvcnN8ZW58MHx8fHwxNzY1OTAxMTkzfDA&ixlib=rb-4.1.0&q=85)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 mb-4 hover:opacity-80">
            <Sparkles className="w-10 h-10 text-primary" />
            <h1 className="text-4xl font-heading font-bold text-foreground">AI Marketing Engine</h1>
          </button>
          <p className="text-muted-foreground">Réinitialisation du mot de passe</p>
        </div>

        <Card className="backdrop-blur-xl bg-white/90 border-white/20 shadow-2xl">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Lock className="w-5 h-5" />
              {success ? 'Mot de passe réinitialisé !' : 'Nouveau mot de passe'}
            </CardTitle>
            <CardDescription>
              {success 
                ? 'Votre mot de passe a été réinitialisé avec succès.'
                : 'Entrez votre nouveau mot de passe ci-dessous.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Success State */}
            {success ? (
              <div className="text-center py-6">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-green-700 font-medium mb-2">
                  Mot de passe modifié avec succès !
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  Redirection vers la page de connexion...
                </p>
                <Button onClick={() => navigate('/auth')} variant="outline">
                  Se connecter maintenant
                </Button>
              </div>
            ) : error && !token ? (
              /* Error State - Invalid Token */
              <div className="text-center py-6">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <p className="text-red-700 font-medium mb-2">
                  Lien invalide
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  {error}
                </p>
                <Button onClick={() => navigate('/forgot-password')} className="w-full mb-2">
                  Demander un nouveau lien
                </Button>
                <Button onClick={() => navigate('/auth')} variant="ghost" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour à la connexion
                </Button>
              </div>
            ) : (
              /* Reset Form */
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="new-password">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Minimum 6 caractères"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirmez votre mot de passe"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Password strength indicator */}
                {newPassword.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      <div className={`h-1 flex-1 rounded ${newPassword.length >= 6 ? 'bg-green-500' : 'bg-gray-200'}`} />
                      <div className={`h-1 flex-1 rounded ${newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-200'}`} />
                      <div className={`h-1 flex-1 rounded ${newPassword.length >= 12 ? 'bg-green-500' : 'bg-gray-200'}`} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {newPassword.length < 6 ? 'Trop court' : 
                       newPassword.length < 8 ? 'Correct' : 
                       newPassword.length < 12 ? 'Fort' : 'Très fort'}
                    </p>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Réinitialisation...
                    </>
                  ) : (
                    'Réinitialiser le mot de passe'
                  )}
                </Button>

                <Button 
                  type="button" 
                  variant="ghost" 
                  className="w-full"
                  onClick={() => navigate('/auth')}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour à la connexion
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Footer links */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <a href="/privacy-policy" className="hover:underline">Confidentialité</a>
          {' • '}
          <a href="/terms" className="hover:underline">CGU</a>
        </div>
      </div>
    </div>
  );
}
